import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import {
    MdClose,
    MdFilePresent,
    MdOutlineErrorOutline,
    MdOutlineUploadFile,
    MdOutlineWarningAmber,
} from 'react-icons/md';
import byteSize from 'byte-size';
import { FormikProps } from 'formik';

export default function Dropzone(props: {
    dropZoneOptions?: DropzoneOptions;
    formats: string;
    formik: FormikProps<any>;
    name: string;
    className?: string;
}) {
    const [files, setFiles] = useState<File[]>([]);

    const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive } =
        useDropzone({ ...props.dropZoneOptions });

    useEffect(() => {
        props.formik.setFieldValue(props.name, files);
    }, [files]);

    useEffect(() => {
        setFiles([...acceptedFiles]);
    }, [acceptedFiles]);

    return (
        <div className={`w-full`}>
            <div
                {...getRootProps()}
                className={`flex h-44 cursor-pointer flex-col items-center justify-center gap-1 rounded border-2 border-dashed p-8 text-gray-600 transition-all
             ${
                 fileRejections.length > 0
                     ? 'border-red-300 bg-red-50'
                     : 'hover:border-blue-300 hover:bg-blue-50'
             }
             ${isDragActive && 'border-blue-300 bg-blue-50'} ${props.className}`}>
                <input {...getInputProps()} />

                {fileRejections.length > 0 ? (
                    <SomeFilesNotAccepted />
                ) : (
                    <UploadFiles formats={props.formats} isDragActive={isDragActive} />
                )}
            </div>
            <AcceptedFiles
                acceptedFiles={files}
                onFileRemove={(index) => {
                    const newArray = files.slice();
                    newArray.splice(index, 1);
                    setFiles(newArray);
                }}
            />
            <RejectedFiles fileRejections={[...fileRejections]} />
        </div>
    );
}

function UploadFiles(props: { isDragActive: boolean; formats: string }) {
    const { t } = useTranslation();

    return (
        <>
            <MdOutlineUploadFile className={'shrink-0 text-4xl'} />
            {props.isDragActive ? (
                <>
                    <p className={'text-center'}>{t('translation:DROPZONE.RELEASE_FILES')}</p>
                    <small className={'text-center text-gray-400 '}>
                        {t('translation:DROPZONE.RELEASE_FILES_HERE')}
                    </small>
                </>
            ) : (
                <>
                    <p className={'text-center'}>{t('translation:DROPZONE.SELECT_FILES')}</p>
                    <small className={'text-center text-gray-400 '}>
                        {t('translation:DROPZONE.DRAG_FILES_HERE', {
                            formats: props.formats,
                        })}
                    </small>
                </>
            )}
        </>
    );
}

function SomeFilesNotAccepted() {
    const { t } = useTranslation();
    return (
        <>
            <MdOutlineWarningAmber className={'shrink-0 text-4xl'} />
            <p className={'text-center'}>{t('translation:DROPZONE.INVALID_FILES')}</p>
            <small className={'text-center text-gray-400 '}>
                {t('translation:DROPZONE.SOME_FILES_ARE_UNLOADED')}
            </small>
        </>
    );
}

function AcceptedFiles(props: { acceptedFiles: File[]; onFileRemove: (index: number) => void }) {
    const acceptedFiles = props.acceptedFiles;
    const { t } = useTranslation();
    return (
        <>
            {acceptedFiles.length > 0 && (
                <p className={'mt-2 font-medium'}>{t('translation:DROPZONE.UPLOADED_FILES')}</p>
            )}

            {acceptedFiles.map((file, index) => {
                return (
                    <div
                        key={`addedFile-${index}`}
                        className={'mt-2 flex items-center gap-2 rounded bg-blue-50 px-4 py-2'}>
                        <MdFilePresent className={'shrink-0 text-3xl text-blue-400'} />
                        <div className={' shrink overflow-hidden'}>
                            <p className={'truncate text-sm'}>{file.name}</p>
                            <p className={'text-xs'}>{byteSize(file.size).toString()}</p>
                        </div>
                        <MdClose
                            className={'ml-auto shrink-0 cursor-pointer hover:text-blue-300'}
                            onClick={() => props.onFileRemove(index)}
                        />
                    </div>
                );
            })}
        </>
    );
}

function RejectedFiles(props: { fileRejections: FileRejection[] }) {
    const fileRejections = props.fileRejections;
    const { t } = useTranslation();
    return (
        <>
            {fileRejections.length > 0 && (
                <p className={'mt-2 font-medium'}>{t('translation:DROPZONE.REJECTED_FILES')}</p>
            )}

            {fileRejections.map((file, index) => {
                return (
                    <div
                        key={`rejectedFile-${index}`}
                        className={'mt-2 flex items-center gap-2 rounded bg-red-50 px-4 py-2'}>
                        <MdOutlineErrorOutline className={'shrink-0 text-3xl text-red-500'} />
                        <div className={' shrink overflow-hidden'}>
                            <p className={'truncate text-sm'}>{file.file.name}</p>
                            <p className={'text-xs text-red-500'}>
                                {t(`translation:ERRORS.FILE_UPLOAD_ERROR.${file.errors[0].code}`)}
                            </p>
                        </div>
                    </div>
                );
            })}
        </>
    );
}
